import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Users, Plus, Search, Edit2, Trash2, ChevronRight,
  Building2, User as UserIcon, Briefcase, FileText, X, Eye, Settings2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { apiFetch, API } from "@/utils/api";
import { getCompanyProfile, getInvoiceDefaults } from "@/utils/companyProfile";
import { formatInvoiceLabel } from "@/utils/invoiceFormat";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [editingCatalogEntry, setEditingCatalogEntry] = useState(null);
  const [catalogForm, setCatalogForm] = useState({ name: "", duration_months: "", default_price: "" });
  // Campañas: módulo interno NEXO (decisión §5 2026-07-02); los tenants de producto no lo ven.
  const [isLegacy, setIsLegacy] = useState(false);
  // Desglose del servicio con los tipos del perfil fiscal (no 21/7 fijos)
  const [fiscalDefaults, setFiscalDefaults] = useState({ vat_rate: 21, irpf_rate: 7 });
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [form, setForm] = useState({
    name: "", company_type: "empresa", nif: "", email: "", phone: "", address: "", city: "", postal_code: "", notes: "", is_active: true
  });
  const [serviceForm, setServiceForm] = useState({
    client_id: "", service_type: "", service_name: "", start_date: "",
    end_date: "", price_agreed: 0, specifications: ""
  });

  const fetchClients = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/crm/clients`);
      if (res.ok) setClients(await res.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/crm/service-catalog`);
      if (res.ok) setCatalog(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchCatalog();
    // Campañas: módulo interno NEXO (decisión §5 2026-07-02); los tenants de producto no lo ven.
    // has_profile true = usuario de producto; false (o error) = tenant legacy NEXO.
    getCompanyProfile()
      .then(p => setIsLegacy(!p?.has_profile))
      .catch(() => setIsLegacy(true));
    getInvoiceDefaults().then(setFiscalDefaults);
  }, [fetchClients, fetchCatalog]);

  const fetchClientDetail = async (clientId) => {
    try {
      const res = await apiFetch(`${API}/crm/clients/${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setDetailData(data);
        setShowDetail(true);
      }
    } catch (e) { console.error(e); }
  };

  const handleSaveClient = async () => {
    const method = editingClient ? "PUT" : "POST";
    const url = editingClient ? `${API}/crm/clients/${editingClient.client_id}` : `${API}/crm/clients`;
    try {
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editingClient ? "Cliente actualizado" : "Cliente creado");
        setShowForm(false);
        setEditingClient(null);
        setForm({ name: "", company_type: "empresa", nif: "", email: "", phone: "", address: "", city: "", postal_code: "", notes: "", is_active: true });
        fetchClients();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error");
      }
    } catch (e) { toast.error("Error de conexion"); }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Eliminar cliente?")) return;
    try {
      const res = await apiFetch(`${API}/crm/clients/${clientId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Cliente eliminado"); fetchClients(); }
    } catch (e) { toast.error("Error"); }
  };

  const handleSaveService = async () => {
    const method = editingService ? "PUT" : "POST";
    const url = editingService ? `${API}/crm/services/${editingService.service_id}` : `${API}/crm/services`;
    const payload = { ...serviceForm, price_agreed: parseFloat(serviceForm.price_agreed) || 0 };
    try {
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editingService ? "Servicio actualizado" : "Servicio creado");
        setShowServiceForm(false);
        setEditingService(null);
        fetchClientDetail(serviceForm.client_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error");
      }
    } catch (e) { toast.error("Error"); }
  };

  const handleDeleteService = async (serviceId, clientId) => {
    // Campañas: módulo interno NEXO (decisión §5 2026-07-02); los tenants de producto no lo ven.
    if (!window.confirm(isLegacy ? "Eliminar servicio y sus campañas?" : "¿Eliminar servicio?")) return;
    try {
      const res = await apiFetch(`${API}/crm/services/${serviceId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Servicio eliminado"); fetchClientDetail(clientId); }
    } catch (e) { toast.error("Error"); }
  };

  const handleUpdateServiceStatus = async (serviceId, status, clientId) => {
    try {
      const res = await apiFetch(`${API}/crm/services/${serviceId}/status?status=${status}`, { method: "PUT" });
      if (res.ok) { toast.success("Estado actualizado"); fetchClientDetail(clientId); }
    } catch (e) { toast.error("Error"); }
  };

  // --- Catálogo de tipos de servicio (GET/POST/PUT/DELETE /crm/service-catalog) ---
  const resetCatalogForm = () => {
    setEditingCatalogEntry(null);
    setCatalogForm({ name: "", duration_months: "", default_price: "" });
  };

  const handleSaveCatalogEntry = async () => {
    if (!catalogForm.name.trim()) return;
    const payload = {
      name: catalogForm.name.trim(),
      duration_months: catalogForm.duration_months === "" ? null : parseInt(catalogForm.duration_months, 10),
      default_price: catalogForm.default_price === "" ? null : parseFloat(catalogForm.default_price),
    };
    const method = editingCatalogEntry ? "PUT" : "POST";
    const url = editingCatalogEntry
      ? `${API}/crm/service-catalog/${editingCatalogEntry.catalog_id}`
      : `${API}/crm/service-catalog`;
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(editingCatalogEntry ? "Tipo de servicio actualizado" : "Tipo de servicio creado");
        resetCatalogForm();
        fetchCatalog();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar el tipo de servicio");
      }
    } catch (e) { toast.error("Error de conexión"); }
  };

  const handleToggleCatalogEntry = async (entry) => {
    try {
      const res = await apiFetch(`${API}/crm/service-catalog/${entry.catalog_id}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: !entry.is_active }),
      });
      if (res.ok) {
        toast.success(entry.is_active ? "Tipo desactivado" : "Tipo activado");
        fetchCatalog();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error");
      }
    } catch (e) { toast.error("Error de conexión"); }
  };

  const handleDeleteCatalogEntry = async (entry) => {
    if (!window.confirm(`¿Borrar el tipo de servicio "${entry.name}"?`)) return;
    try {
      const res = await apiFetch(`${API}/crm/service-catalog/${entry.catalog_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Tipo de servicio borrado");
        fetchCatalog();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al borrar");
      }
    } catch (e) { toast.error("Error de conexión"); }
  };

  const activeCatalog = catalog.filter(c => c.is_active);

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.nif || "").toLowerCase().includes(search.toLowerCase())
  );

  const statusColors = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    completed: "bg-slate-100 text-slate-600 border-slate-200",
    cancelled: "bg-red-50 text-red-600 border-red-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    planned: "bg-blue-50 text-blue-700 border-blue-200",
  };

  const statusLabels = {
    active: "Activo", completed: "Completado", cancelled: "Cancelado", paused: "Pausado", planned: "Planificada"
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="clients-title">Clientes</h1>
          {/* Campañas: módulo interno NEXO (decisión §5 2026-07-02); los tenants de producto no lo ven */}
          <p className="text-sm text-slate-500 mt-1">{isLegacy ? "Gestión de clientes, servicios y campañas" : "Gestión de clientes y servicios"}</p>
        </div>
        <Button
          data-testid="add-client-btn"
          onClick={() => { setEditingClient(null); setForm({ name: "", company_type: "empresa", nif: "", email: "", phone: "", address: "", city: "", postal_code: "", notes: "", is_active: true }); setShowForm(true); }}
          className="bg-slate-900 hover:bg-slate-800 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo Cliente
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="client-search"
          placeholder="Buscar por nombre o NIF..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Clients Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay clientes</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => (
            <div
              key={c.client_id}
              className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer group"
              data-testid={`client-card-${c.client_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    {c.company_type === "empresa" ? <Building2 className="w-5 h-5 text-slate-600" /> : <UserIcon className="w-5 h-5 text-slate-600" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 text-sm">{c.name}</h3>
                      {c.is_active === false && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">Inactivo</span>}
                    </div>
                    {c.nif && <p className="text-xs font-mono text-slate-500">{c.nif}</p>}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-client-${c.client_id}`} onClick={e => { e.stopPropagation(); setEditingClient(c); setForm({ name: c.name, company_type: c.company_type, nif: c.nif || "", email: c.email || "", phone: c.phone || "", address: c.address || "", city: c.city || "", postal_code: c.postal_code || "", notes: c.notes || "", is_active: c.is_active !== false }); setShowForm(true); }} className="p-1.5 rounded hover:bg-slate-100"><Edit2 className="w-3.5 h-3.5 text-slate-500" /></button>
                  <button data-testid={`delete-client-${c.client_id}`} onClick={e => { e.stopPropagation(); handleDeleteClient(c.client_id); }} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-xs text-slate-500">Servicios</p>
                  <p className="font-mono font-medium text-sm">{c.active_services || 0}/{c.total_services || 0}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-xs text-slate-500">Fact. Pend.</p>
                  <p className="font-mono font-medium text-sm">{c.pending_invoices || 0}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-xs text-slate-500">Tipo</p>
                  <p className="text-xs font-medium capitalize">{c.company_type}</p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                data-testid={`view-client-${c.client_id}`}
                className="w-full justify-between text-slate-600 hover:text-slate-900"
                onClick={() => fetchClientDetail(c.client_id)}
              >
                Ver detalle <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Client Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingClient ? "Editar Cliente" : "Nuevo Cliente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Nombre *</label>
              <Input data-testid="client-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nombre de la empresa o profesional" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Tipo</label>
                <Select value={form.company_type} onValueChange={v => setForm({ ...form, company_type: v })}>
                  <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="empresa">Empresa</SelectItem>
                    <SelectItem value="profesional">Profesional</SelectItem>
                    <SelectItem value="autonomo">Autonomo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">NIF/CIF</label>
                <Input data-testid="client-nif-input" value={form.nif} onChange={e => setForm({ ...form, nif: e.target.value })} placeholder="B12345678" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Email</label>
                <Input data-testid="client-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Telefono</label>
                <Input data-testid="client-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Direccion</label>
              <Input data-testid="client-address-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Ciudad</label>
                <Input data-testid="client-city-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Ej: Madrid" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Codigo Postal</label>
                <Input data-testid="client-postal-input" value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} placeholder="Ej: 28001" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notas</label>
              <textarea data-testid="client-notes-input" className="w-full border border-slate-200 rounded-md p-2 text-sm" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid="client-active-toggle"
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
                className="w-4 h-4 accent-slate-900"
              />
              <span>Cliente activo</span>
              <span className="text-xs text-slate-500">(desmarcar si ya no trabajas con este cliente)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button data-testid="save-client-btn" onClick={handleSaveClient} disabled={!form.name} className="bg-slate-900 hover:bg-slate-800 text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="client-detail-dialog">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    {detailData.company_type === "empresa" ? <Building2 className="w-5 h-5 text-slate-600" /> : <UserIcon className="w-5 h-5 text-slate-600" />}
                  </div>
                  <div>
                    <div>{detailData.name}</div>
                    {detailData.nif && <span className="text-xs font-mono text-slate-500 font-normal">{detailData.nif}</span>}
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* KPIs */}
              <div className="grid grid-cols-4 gap-3 my-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-emerald-600 mb-1">Ingresos</p>
                  <p className="font-mono font-semibold text-lg text-emerald-700">{detailData.total_revenue?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600 mb-1">Costes Directos</p>
                  <p className="font-mono font-semibold text-lg text-red-600">{detailData.total_direct_costs?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600 mb-1">Margen</p>
                  <p className="font-mono font-semibold text-lg text-blue-700">{detailData.margin?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Cobrado</p>
                  <p className="font-mono font-semibold text-lg">{detailData.total_collected?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</p>
                </div>
              </div>

              {/* Services */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-slate-900">Servicios</h3>
                  <Button size="sm" data-testid="add-service-btn" onClick={() => {
                    setEditingService(null);
                    setServiceForm({ client_id: detailData.client_id, service_type: "", service_name: "", start_date: "", end_date: "", price_agreed: 0, specifications: "" });
                    setShowServiceForm(true);
                  }} className="bg-slate-900 hover:bg-slate-800 text-white">
                    <Plus className="w-3 h-3 mr-1" /> Servicio
                  </Button>
                </div>

                {detailData.services?.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded">No hay servicios</p>
                ) : (
                  <div className="space-y-3">
                    {detailData.services?.map(svc => (
                      <div key={svc.service_id} className="border border-slate-200 rounded-lg p-4" data-testid={`service-${svc.service_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-slate-500" />
                            <span className="font-medium text-sm">{svc.service_name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColors[svc.status] || "bg-slate-100"}`}>
                              {statusLabels[svc.status] || svc.status}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <Select value={svc.status} onValueChange={v => handleUpdateServiceStatus(svc.service_id, v, detailData.client_id)}>
                              <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Activo</SelectItem>
                                <SelectItem value="completed">Completado</SelectItem>
                                <SelectItem value="paused">Pausado</SelectItem>
                                <SelectItem value="cancelled">Cancelado</SelectItem>
                              </SelectContent>
                            </Select>
                            <button onClick={() => { setEditingService(svc); setServiceForm({ client_id: detailData.client_id, service_type: svc.service_type, service_name: svc.service_name, start_date: svc.start_date?.slice(0, 10) || "", end_date: svc.end_date?.slice(0, 10) || "", price_agreed: svc.price_agreed || 0, specifications: svc.specifications || "" }); setShowServiceForm(true); }} className="p-1 rounded hover:bg-slate-100"><Edit2 className="w-3.5 h-3.5 text-slate-500" /></button>
                            <button onClick={() => handleDeleteService(svc.service_id, detailData.client_id)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                          <div><span className="text-slate-500">Inicio:</span> <span className="font-mono">{svc.start_date?.slice(0, 10)}</span></div>
                          <div><span className="text-slate-500">Fin:</span> <span className="font-mono">{svc.end_date?.slice(0, 10) || "—"}</span></div>
                          <div>
                            <span className="text-slate-500">B.I.:</span> <span className="font-mono">{svc.price_agreed?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</span>
                            <span className="text-slate-400 ml-1">(Total: {((svc.price_agreed || 0) * (1 + (fiscalDefaults.vat_rate - fiscalDefaults.irpf_rate) / 100)).toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR)</span>
                          </div>
                        </div>

                        {svc.specifications && <p className="text-xs text-slate-500 mb-2 italic">{svc.specifications}</p>}

                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invoices */}
              {detailData.invoices?.length > 0 && (
                <div>
                  <h3 className="font-heading font-semibold text-slate-900 mb-2">Facturas Emitidas</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="px-3 py-2 text-left">Numero</th>
                          <th className="px-3 py-2 text-left">Fecha</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2 text-center">Estado Pago</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.invoices.map(inv => (
                          <tr key={inv.invoice_id} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-mono text-xs">{formatInvoiceLabel(inv.series, inv.invoice_number)}</td>
                            <td className="px-3 py-2 font-mono text-xs">{inv.invoice_date?.slice(0, 10)}</td>
                            <td className="px-3 py-2 font-mono text-xs text-right">{inv.total_amount?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${inv.payment_status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : inv.payment_status === "partial" ? "bg-amber-50 text-amber-700 border-amber-200" : inv.payment_status === "compensated" ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-red-50 text-red-600 border-red-200"}`}>
                                {inv.payment_status === "paid" ? "Pagada" : inv.payment_status === "partial" ? "Parcial" : inv.payment_status === "compensated" ? "Compensada" : "Pendiente"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Gastos imputados a este cliente */}
              {detailData.expenses?.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-heading font-semibold text-slate-900 mb-2">Gastos imputados</h3>
                  <p className="text-xs text-slate-500 mb-2">
                    Gastos directos asignados a este cliente desde Facturas Recibidas.
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="px-3 py-2 text-left">Fecha</th>
                          <th className="px-3 py-2 text-left">Proveedor</th>
                          <th className="px-3 py-2 text-right">Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.expenses.map((exp, i) => (
                          <tr key={exp.split_id || i} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-mono text-xs">{exp.invoice_date?.slice(0, 10) || "—"}</td>
                            <td className="px-3 py-2 text-xs">{exp.supplier_name || "—"}</td>
                            <td className="px-3 py-2 font-mono text-xs text-right">{(exp.cost_eur ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-slate-200 bg-slate-50">
                          <td className="px-3 py-2 text-xs font-semibold text-slate-600" colSpan={2}>Total imputado</td>
                          <td className="px-3 py-2 font-mono text-xs text-right font-semibold text-red-600">{detailData.total_direct_costs?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Service Form Dialog */}
      <Dialog open={showServiceForm} onOpenChange={setShowServiceForm}>
        <DialogContent className="max-w-lg" data-testid="service-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingService ? "Editar Servicio" : "Nuevo Servicio"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium block">Tipo de Servicio *</label>
                <button
                  type="button"
                  data-testid="service-catalog-manage-btn"
                  onClick={() => { resetCatalogForm(); setShowCatalog(true); }}
                  className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                >
                  <Settings2 className="w-3 h-3" /> Gestionar catálogo
                </button>
              </div>
              {activeCatalog.length === 0 && !serviceForm.service_type ? (
                <div
                  data-testid="service-catalog-empty-state"
                  className="border border-dashed border-slate-300 rounded-md p-4 text-center"
                >
                  <p className="text-sm text-slate-500 mb-2">Aún no tienes tipos de servicio en el catálogo.</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="service-catalog-create-first-btn"
                    onClick={() => { resetCatalogForm(); setShowCatalog(true); }}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Crear el primer tipo de servicio
                  </Button>
                </div>
              ) : (
                <Select
                  value={serviceForm.service_type}
                  onValueChange={v => {
                    const entry = catalog.find(c => c.catalog_id === v);
                    setServiceForm(prev => ({
                      ...prev,
                      service_type: v,
                      service_name: entry ? entry.name : prev.service_name,
                      // Prefija el precio por defecto del catálogo solo si el precio sigue vacío/0
                      price_agreed:
                        entry?.default_price != null && !(parseFloat(prev.price_agreed) > 0)
                          ? entry.default_price
                          : prev.price_agreed,
                    }));
                  }}
                >
                  <SelectTrigger data-testid="service-type-select">
                    <SelectValue placeholder="Selecciona un tipo del catálogo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCatalog.map(entry => (
                      <SelectItem key={entry.catalog_id} value={entry.catalog_id}>
                        {entry.name}
                        {entry.duration_months ? ` · ${entry.duration_months} mes${entry.duration_months !== 1 ? "es" : ""}` : ""}
                      </SelectItem>
                    ))}
                    {/* Tipo legado no presente en el catálogo activo (servicios antiguos): se muestra para no perderlo */}
                    {serviceForm.service_type && !activeCatalog.some(c => c.catalog_id === serviceForm.service_type) && (
                      <SelectItem value={serviceForm.service_type}>
                        {serviceForm.service_name || serviceForm.service_type}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Fecha Inicio *</label>
                <Input data-testid="service-start-input" type="date" value={serviceForm.start_date} onChange={e => setServiceForm({ ...serviceForm, start_date: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Fecha Fin</label>
                <Input data-testid="service-end-input" type="date" value={serviceForm.end_date} onChange={e => setServiceForm({ ...serviceForm, end_date: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Base Imponible (EUR)</label>
              <Input data-testid="service-price-input" type="number" step="0.01" value={serviceForm.price_agreed} onChange={e => setServiceForm({ ...serviceForm, price_agreed: e.target.value })} placeholder="Precio sin impuestos" />
              {parseFloat(serviceForm.price_agreed) > 0 && (
                <div className="mt-2 bg-slate-50 border border-slate-200 rounded-md p-3 text-sm font-mono space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Base Imponible:</span><span>{parseFloat(serviceForm.price_agreed).toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">+ IVA ({fiscalDefaults.vat_rate}%):</span><span>{(parseFloat(serviceForm.price_agreed) * fiscalDefaults.vat_rate / 100).toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</span></div>
                  {fiscalDefaults.irpf_rate > 0 && (
                    <div className="flex justify-between"><span className="text-slate-500">- IRPF ({fiscalDefaults.irpf_rate}%):</span><span>-{(parseFloat(serviceForm.price_agreed) * fiscalDefaults.irpf_rate / 100).toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</span></div>
                  )}
                  <div className="flex justify-between border-t border-slate-300 pt-1 font-semibold"><span className="text-slate-700">Total cliente:</span><span>{(parseFloat(serviceForm.price_agreed) * (1 + (fiscalDefaults.vat_rate - fiscalDefaults.irpf_rate) / 100)).toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</span></div>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Especificaciones</label>
              <textarea data-testid="service-specs-input" className="w-full border border-slate-200 rounded-md p-2 text-sm" rows={3} value={serviceForm.specifications} onChange={e => setServiceForm({ ...serviceForm, specifications: e.target.value })} placeholder="Detalles del servicio..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceForm(false)}>Cancelar</Button>
            <Button data-testid="save-service-btn" onClick={handleSaveService} disabled={!serviceForm.service_type || !serviceForm.start_date} className="bg-slate-900 hover:bg-slate-800 text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Catalog Dialog (CRUD) */}
      <Dialog open={showCatalog} onOpenChange={v => { setShowCatalog(v); if (!v) resetCatalogForm(); }}>
        <DialogContent className="max-w-xl" data-testid="service-catalog-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Catálogo de tipos de servicio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {catalog.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded">
                El catálogo está vacío. Crea tu primer tipo de servicio abajo.
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Nombre</th>
                      <th className="px-3 py-2 text-right">Duración</th>
                      <th className="px-3 py-2 text-right">Precio por defecto</th>
                      <th className="px-3 py-2 text-center">Estado</th>
                      <th className="px-3 py-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalog.map(entry => (
                      <tr key={entry.catalog_id} className={`border-t border-slate-100 ${entry.is_active ? "" : "opacity-60"}`} data-testid={`catalog-row-${entry.catalog_id}`}>
                        <td className="px-3 py-2">{entry.name}</td>
                        <td className="px-3 py-2 font-mono text-xs text-right">{entry.duration_months != null ? `${entry.duration_months} mes${entry.duration_months !== 1 ? "es" : ""}` : "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs text-right">{entry.default_price != null ? `${entry.default_price.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR` : "—"}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${entry.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                            {entry.is_active ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-1">
                            <button
                              data-testid={`catalog-edit-${entry.catalog_id}`}
                              onClick={() => {
                                setEditingCatalogEntry(entry);
                                setCatalogForm({
                                  name: entry.name || "",
                                  duration_months: entry.duration_months ?? "",
                                  default_price: entry.default_price ?? "",
                                });
                              }}
                              className="p-1 rounded hover:bg-slate-100"
                              title="Editar"
                            ><Edit2 className="w-3.5 h-3.5 text-slate-500" /></button>
                            <button
                              data-testid={`catalog-toggle-${entry.catalog_id}`}
                              onClick={() => handleToggleCatalogEntry(entry)}
                              className="p-1 rounded hover:bg-slate-100"
                              title={entry.is_active ? "Desactivar" : "Activar"}
                            >{entry.is_active ? <X className="w-3.5 h-3.5 text-amber-500" /> : <Eye className="w-3.5 h-3.5 text-emerald-600" />}</button>
                            <button
                              data-testid={`catalog-delete-${entry.catalog_id}`}
                              onClick={() => handleDeleteCatalogEntry(entry)}
                              className="p-1 rounded hover:bg-red-50"
                              title="Borrar"
                            ><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Alta / edición */}
            <div className="border border-slate-200 rounded-lg p-3 space-y-3">
              <p className="text-sm font-medium">{editingCatalogEntry ? `Editar "${editingCatalogEntry.name}"` : "Nuevo tipo de servicio"}</p>
              <div>
                <label className="text-sm font-medium mb-1 block">Nombre *</label>
                <Input
                  data-testid="catalog-name-input"
                  value={catalogForm.name}
                  onChange={e => setCatalogForm({ ...catalogForm, name: e.target.value })}
                  placeholder="Ej: Ciclo de prospección 3 meses"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Duración (meses)</label>
                  <Input
                    data-testid="catalog-duration-input"
                    type="number"
                    min="1"
                    step="1"
                    value={catalogForm.duration_months}
                    onChange={e => setCatalogForm({ ...catalogForm, duration_months: e.target.value })}
                    placeholder="Ej: 3"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Precio por defecto (EUR)</label>
                  <Input
                    data-testid="catalog-price-input"
                    type="number"
                    step="0.01"
                    value={catalogForm.default_price}
                    onChange={e => setCatalogForm({ ...catalogForm, default_price: e.target.value })}
                    placeholder="Base imponible sugerida"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                {editingCatalogEntry && (
                  <Button variant="outline" size="sm" data-testid="catalog-cancel-edit-btn" onClick={resetCatalogForm}>Cancelar edición</Button>
                )}
                <Button
                  size="sm"
                  data-testid="catalog-save-btn"
                  onClick={handleSaveCatalogEntry}
                  disabled={!catalogForm.name.trim()}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {editingCatalogEntry ? "Guardar cambios" : "Añadir al catálogo"}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatalog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
